import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar, 
  Wallet,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

/**
 * Test d'opacité de la tortue - Comparaison visuelle
 * 
 * Versions testées :
 * - 8% (actuelle)
 * - 10% (+2%)
 * - 11% (+3%)
 * - 12% (+4%)
 */

const TurtleOpacityCard = ({ opacity, label, isActive, onClick }) => {
  const calendarDays = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
  
  return (
    <motion.div 
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      className={`relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-300 ${
        isActive ? 'ring-4 ring-orange-400 ring-offset-2' : ''
      }`}
      style={{
        boxShadow: isActive 
          ? '0 12px 40px rgba(249, 115, 22, 0.3)' 
          : '0 8px 24px rgba(0,0,0,0.1)'
      }}
    >
      {/* Fond blanc */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 40%, #f1f5f9 100%)'
        }}
      />

      {/* TORTUE avec opacité variable */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'url(/turtle-bg.jpg)',
          backgroundSize: '200%',
          backgroundPosition: 'center 30%',
          opacity: opacity,
          filter: 'saturate(0.7) contrast(1.1)'
        }}
      />

      {/* Accent océan */}
      <div 
        className="absolute top-0 left-0 right-0 h-32 pointer-events-none"
        style={{
          background: 'linear-gradient(180deg, rgba(14, 116, 144, 0.08) 0%, transparent 100%)'
        }}
      />

      {/* Contenu */}
      <div className="relative z-10 p-4">
        {/* Badge opacité */}
        <div 
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-3"
          style={{
            background: isActive 
              ? 'linear-gradient(135deg, #f97316 0%, #f43f5e 100%)'
              : 'linear-gradient(135deg, #0e7490 0%, #0369a1 100%)',
            color: 'white'
          }}
        >
          🐢 {label}
        </div>

        {/* Mini header */}
        <div className="flex items-center gap-2 mb-3">
          <div 
            className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-bold"
            style={{ background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 100%)' }}
          >
            DU
          </div>
          <span className="text-slate-700 text-sm font-semibold">Mon Budget</span>
        </div>

        {/* Mini cartes résumé */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div 
            className="p-2 rounded-xl text-center"
            style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #f0fdf4 100%)',
              border: '1px solid rgba(16, 185, 129, 0.15)'
            }}
          >
            <TrendingUp className="w-3 h-3 text-emerald-500 mx-auto mb-1" />
            <p className="text-[10px] text-emerald-600 font-bold">+2800€</p>
          </div>
          <div 
            className="p-2 rounded-xl text-center"
            style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #fff1f2 100%)',
              border: '1px solid rgba(244, 63, 94, 0.15)'
            }}
          >
            <TrendingDown className="w-3 h-3 text-rose-500 mx-auto mb-1" />
            <p className="text-[10px] text-rose-500 font-bold">-978€</p>
          </div>
          <div 
            className="p-2 rounded-xl text-center"
            style={{ background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 100%)' }}
          >
            <Wallet className="w-3 h-3 text-white mx-auto mb-1" />
            <p className="text-[10px] text-white font-bold">+2500€</p>
          </div>
        </div>

        {/* Mini calendrier */}
        <div 
          className="rounded-2xl overflow-hidden"
          style={{
            boxShadow: '0 4px 16px rgba(14, 116, 144, 0.12)',
            border: '1px solid rgba(14, 116, 144, 0.1)'
          }}
        >
          <div 
            className="p-2 flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 50%, #1e3a5f 100%)' }}
          >
            <ChevronLeft className="w-4 h-4 text-white/70" />
            <span className="text-white text-xs font-bold">Avril 2026</span>
            <ChevronRight className="w-4 h-4 text-white/70" />
          </div>
          <div className="p-2 bg-gradient-to-b from-slate-50/50 to-white">
            <div className="grid grid-cols-5 gap-1">
              {calendarDays.map((day) => (
                <div 
                  key={day}
                  className="aspect-square rounded-lg flex flex-col items-center justify-center text-[9px]"
                  style={
                    day === 8
                      ? {
                          background: 'linear-gradient(135deg, #f97316 0%, #f43f5e 100%)',
                          color: 'white',
                          fontWeight: 'bold'
                        }
                      : {
                          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                          border: '1px solid rgba(148, 163, 184, 0.2)',
                          color: '#475569'
                        }
                  }
                >
                  {day}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const TurtleOpacityTest = () => {
  const [selectedOpacity, setSelectedOpacity] = useState(0.08);

  const opacityOptions = [
    { value: 0.08, label: '8% (actuel)', description: 'Subtile, discrète' },
    { value: 0.10, label: '10% (+2%)', description: 'Légèrement plus visible' },
    { value: 0.11, label: '11% (+3%)', description: 'Présence modérée' },
    { value: 0.12, label: '12% (+4%)', description: 'Plus marquée' },
  ];

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <h1 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          🐢 Test d'opacité - Tortue
        </h1>
        <p className="text-slate-600" style={{ fontFamily: 'Figtree, sans-serif' }}>
          Comparez les différentes variations pour trouver l'équilibre parfait entre subtilité et présence.
        </p>
      </div>

      {/* Grille de comparaison */}
      <div className="max-w-4xl mx-auto grid grid-cols-2 gap-6 mb-8">
        {opacityOptions.map((option) => (
          <TurtleOpacityCard
            key={option.value}
            opacity={option.value}
            label={option.label}
            isActive={selectedOpacity === option.value}
            onClick={() => setSelectedOpacity(option.value)}
          />
        ))}
      </div>

      {/* Légende */}
      <div className="max-w-4xl mx-auto">
        <div 
          className="p-4 rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
          }}
        >
          <h3 className="font-bold text-slate-700 mb-3">📊 Comparaison</h3>
          <div className="grid grid-cols-4 gap-4">
            {opacityOptions.map((option) => (
              <div 
                key={option.value}
                className={`p-3 rounded-xl transition-all ${
                  selectedOpacity === option.value 
                    ? 'bg-orange-50 border-2 border-orange-300' 
                    : 'bg-slate-50 border border-slate-200'
                }`}
              >
                <p className="font-bold text-sm text-slate-700">{option.label}</p>
                <p className="text-xs text-slate-500 mt-1">{option.description}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-4 p-3 bg-cyan-50 rounded-xl border border-cyan-200">
            <p className="text-sm text-cyan-800">
              <strong>💡 Conseil :</strong> Cliquez sur une carte pour la sélectionner (bordure orange). 
              Observez l'impact de la tortue sur la lisibilité des textes et l'ambiance générale.
            </p>
          </div>
        </div>
      </div>

      {/* Preview plein écran de la version sélectionnée */}
      <div className="max-w-4xl mx-auto mt-8">
        <h3 className="font-bold text-slate-700 mb-4">🔍 Preview grande taille - {opacityOptions.find(o => o.value === selectedOpacity)?.label}</h3>
        <div 
          className="relative rounded-3xl overflow-hidden"
          style={{
            height: '500px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
          }}
        >
          {/* Fond blanc */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 40%, #f1f5f9 100%)'
            }}
          />

          {/* TORTUE */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: 'url(/turtle-bg.jpg)',
              backgroundSize: '120%',
              backgroundPosition: 'center 20%',
              opacity: selectedOpacity,
              filter: 'saturate(0.7) contrast(1.1)'
            }}
          />

          {/* Accent océan */}
          <div 
            className="absolute top-0 left-0 right-0 h-48 pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, rgba(14, 116, 144, 0.08) 0%, transparent 100%)'
            }}
          />

          {/* Contenu simulé */}
          <div className="relative z-10 p-8">
            <div className="flex items-center gap-4 mb-6">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold"
                style={{ 
                  background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 100%)',
                  boxShadow: '0 8px 24px rgba(14, 116, 144, 0.35)'
                }}
              >
                DU
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Content de te revoir, Démo 👋</h2>
                <p className="text-slate-500">Prêt à gérer ton budget ?</p>
              </div>
            </div>

            {/* Cartes */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div 
                className="p-5 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, #ffffff 0%, #f0fdf4 100%)',
                  boxShadow: '0 4px 16px rgba(16, 185, 129, 0.12)',
                  border: '1px solid rgba(16, 185, 129, 0.15)'
                }}
              >
                <p className="text-xs text-emerald-600/70 uppercase tracking-wider mb-1 font-semibold">Entrées</p>
                <p className="text-2xl font-extrabold text-emerald-600">+2800€</p>
              </div>
              <div 
                className="p-5 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, #ffffff 0%, #fff1f2 100%)',
                  boxShadow: '0 4px 16px rgba(244, 63, 94, 0.12)',
                  border: '1px solid rgba(244, 63, 94, 0.15)'
                }}
              >
                <p className="text-xs text-rose-500/70 uppercase tracking-wider mb-1 font-semibold">Sorties</p>
                <p className="text-2xl font-extrabold text-rose-500">-978€</p>
              </div>
              <div 
                className="p-5 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, #0e7490 0%, #0369a1 100%)',
                  boxShadow: '0 8px 24px rgba(14, 116, 144, 0.35)'
                }}
              >
                <p className="text-xs text-cyan-100/80 uppercase tracking-wider mb-1 font-semibold">Solde</p>
                <p className="text-2xl font-extrabold text-white">+2500€</p>
              </div>
            </div>

            {/* Calendrier header */}
            <div 
              className="rounded-2xl overflow-hidden"
              style={{
                boxShadow: '0 12px 40px rgba(14, 116, 144, 0.15)',
                border: '1px solid rgba(14, 116, 144, 0.1)'
              }}
            >
              <div 
                className="p-4 flex items-center justify-between"
                style={{ background: 'linear-gradient(135deg, #0e7490 0%, #0369a1 50%, #1e3a5f 100%)' }}
              >
                <button className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center text-white">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="text-white font-bold text-lg">Avril 2026</h3>
                <button className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center text-white">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 bg-gradient-to-b from-slate-50/50 to-white">
                <p className="text-center text-slate-500 text-sm">Grille du calendrier...</p>
              </div>
            </div>
          </div>

          {/* Badge opacité */}
          <div 
            className="absolute top-4 right-4 px-4 py-2 rounded-full text-white text-sm font-bold"
            style={{ background: 'linear-gradient(135deg, #f97316 0%, #f43f5e 100%)' }}
          >
            🐢 Opacité: {Math.round(selectedOpacity * 100)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default TurtleOpacityTest;
